import { create, type StoreApi, type UseBoundStore } from 'zustand';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export interface DocumentStoreState<T> {
  id: string | null;
  document: T | null;
  /** The server's `version` for optimistic concurrency (`If-Match`). */
  version: number;
  saveState: SaveState;
  error: string | null;
  load: (id: string, document: T, version: number) => void;
  clear: () => void;
  /** Shallow-merges into the open document and marks it dirty for the autosave debounce to pick up. */
  patch: (partial: Partial<T>) => void;
  setSaveState: (state: SaveState, error?: string | null) => void;
  /** Reconciles a completed save without marking edits made while it was in flight as saved. */
  markSaved: (version: number, savedDocument?: T) => void;
}

/**
 * One shape backs both the mission and rocket editors: an open document
 * identified by server id, its optimistic-concurrency version, and a save
 * state the autosave hook and status bar can read. Kept generic because
 * Mission and Rocket genuinely need the identical lifecycle — load, edit,
 * debounce-save, reconcile version — and only differ in the payload type.
 */
export function createDocumentStore<T>(): UseBoundStore<StoreApi<DocumentStoreState<T>>> {
  return create<DocumentStoreState<T>>((set) => ({
    id: null,
    document: null,
    version: 0,
    saveState: 'idle',
    error: null,
    load: (id, document, version) => set({ id, document, version, saveState: 'idle', error: null }),
    clear: () => set({ id: null, document: null, version: 0, saveState: 'idle', error: null }),
    patch: (partial) =>
      set((state) => ({
        document: state.document ? { ...state.document, ...partial } : state.document,
        saveState: 'dirty',
      })),
    setSaveState: (saveState, error = null) => set({ saveState, error }),
    markSaved: (version, savedDocument) =>
      set((state) => ({
        version,
        saveState:
          savedDocument === undefined || state.document === savedDocument
            ? 'saved'
            : state.saveState,
        error: null,
      })),
  }));
}
